import React, { useState, useEffect, Suspense, lazy } from 'react';
import { base44 } from '@/api/base44Client';
import { withRateLimitRetry } from '@/lib/rateLimitRetry';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { User } from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import SideDrawer from '../components/SideDrawer';
import BroadcastCarousel from '../components/BroadcastCarousel';
import HeroSection from '../components/HeroSection';
import { rebuildHomeCache } from '@/lib/homeCache';
import AppHomeSections from '@/components/home/AppHomeSections';

// App Home — the Base44-domain / native app homepage. Cloned from Home so the
// app experience can evolve independently of the custom-domain website.
const InstagramCarousel = lazy(() => import('../components/InstagramCarousel'));
const CarouselLoader = () => <div className="h-64 bg-gray-100 animate-pulse" />;

const HERO_CACHE_KEY = 'home_hero_cache';

const readCachedHero = () => {
  try {
    const raw = localStorage.getItem(HERO_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCachedHero = (hero) => {
  try {
    if (hero) localStorage.setItem(HERO_CACHE_KEY, JSON.stringify(hero));
    else localStorage.removeItem(HERO_CACHE_KEY);
  } catch {
    // best-effort cache
  }
};

export default function AppHome() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [businessName, setBusinessName] = useState(null);
  const [broadcasts, setBroadcasts] = useState(() => {
    try { return JSON.parse(localStorage.getItem('home_broadcasts_cache')) || []; } catch { return []; }
  });
  const [hero, setHero] = useState(readCachedHero);
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirect = params.get('redirect');
    if (redirect && /^\/(?![/\\])/.test(redirect)) {
      navigate(redirect, { replace: true });
      return;
    }
    loadFromCache();
  }, []);

  const loadFromCache = async (forceRebuild = false) => {
    try {
      if (forceRebuild) await rebuildHomeCache();
      const cacheData = await withRateLimitRetry(() => base44.entities.HomeCache.list());
      if (cacheData.length > 0) {
        const cache = cacheData[0];
        setBroadcasts(cache.broadcasts || []);
        try { localStorage.setItem('home_broadcasts_cache', JSON.stringify(cache.broadcasts || [])); } catch { /* best-effort */ }
        setHero(cache.hero || null);
        writeCachedHero(cache.hero || null);
        if (cache.contact_info?.business_name) setBusinessName(cache.contact_info.business_name);
      } else {
        await rebuildHomeCache();
        await loadFromCache(false);
      }
    } catch (error) {
      console.error('Error loading home cache:', error);
    }
    if (!forceRebuild) loadBroadcastsDirect();
  };

  // Reads active broadcasts straight from the source so the section never
  // stays empty if the shared home cache is missing or fails on the device.
  const loadBroadcastsDirect = async () => {
    try {
      const list = await withRateLimitRetry(() => base44.entities.BroadcastNotification.filter({ is_active: true }));
      if (!list?.length) return;
      const sorted = [...list].sort((a, b) => (a.display_order ?? Infinity) - (b.display_order ?? Infinity));
      setBroadcasts(sorted);
      try { localStorage.setItem('home_broadcasts_cache', JSON.stringify(sorted)); } catch { /* best-effort */ }
    } catch (error) {
      console.error('Error loading broadcasts:', error);
    }
  };

  const handleBroadcastClick = (broadcast) => {
    if (!broadcast.link_type || !broadcast.link_value) return;
    if (broadcast.link_type !== 'internal_page') {
      window.location.href = broadcast.link_value;
      return;
    }
    if (broadcast.link_value.includes('||')) {
      const [page, locationId, categoryId] = broadcast.link_value.split('||');
      let url = createPageUrl(page);
      if (locationId) url += `?location=${locationId}`;
      if (categoryId) url += locationId ? `&category=${categoryId}` : `?category=${categoryId}`;
      navigate(url);
    } else {
      navigate(createPageUrl(broadcast.link_value));
    }
  };

  return (
    <div className="min-h-screen bg-white pt-[calc(max(env(safe-area-inset-top),1.5rem)+3.5rem)] pb-40 lg:pt-0 lg:pb-0">
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white z-40 border-b border-gray-100 pt-[max(env(safe-area-inset-top),1.5rem)]">
        <div className="flex items-center justify-between w-full h-12 px-4">
          <NotificationBell />
          <button
            onClick={() => navigate(createPageUrl('Account'))}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors focus-visible:ring-2 focus-visible:ring-pink-300 focus-visible:ring-offset-2"
            aria-label="Account">
            <User className="w-6 h-6 text-black" />
          </button>
        </div>
      </div>

      <SideDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <AppHomeSections hero={hero} broadcasts={broadcasts} onBroadcastClick={handleBroadcastClick} />
    </div>
  );
}